import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Col, Row, Button, Label, Input, Table, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";

/**
 * Tela apartada para pedidos, por cantidad.
 *
 * Pendiente = apartado - consumido (mandado al maquilero de ese pedido) -
 * liberado. Liberar regresa lo pendiente al disponible.
 */
function ListadoApartados() {
  const { user } = useContext(AuthContext);
  const URL_APARTADOS_MATERIALES = process.env.REACT_APP_URL_APARTADOS_MATERIALES;

  const [filas, setFilas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [cargando, setCargando] = useState(false);
  const [recarga, setRecarga] = useState(0);

  const cab = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

  useEffect(() => {
    setCargando(true);
    axios.get(URL_APARTADOS_MATERIALES, cab())
      .then((r) => setFilas(r.data.map((a) => ({
        _id: a._id,
        folio: a.apartados?.idApartado,
        fecha: a.fecha,
        pedido: a.pedidos?.idPedido,
        cliente: a.pedidos?.clientes?.[0]?.nombre_comercial ?? "",
        tela: [a.material?.codigo, a.material?.nombre].filter((x) => x && x !== "NA").join(" "),
        color: a.colores?.name ?? "",
        almacen: a.areas?.name ?? "",
        cantidad: a.cantidad,
        consumido: a.consumido || 0,
        liberado: a.liberado || 0,
        pendiente: a.pendiente,
        observaciones: a.apartados?.observaciones ?? "",
      }))))
      .catch((e) => console.log(e))
      .finally(() => setCargando(false));
  }, [recarga]);

  const texto = busqueda.toLowerCase();
  const visibles = filas
    .filter((f) => !soloPendientes || f.pendiente > 0)
    .filter((f) => !texto || `${f.folio} ${f.pedido} ${f.cliente} ${f.tela} ${f.color}`.toLowerCase().includes(texto));
  const n = (x) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(x || 0);

  async function liberar(f) {
    const r = await Swal.fire({
      title: `Liberar ${f.tela} ${f.color}`,
      text: `Pedido ${f.pedido}: quedan ${f.pendiente} apartados. ¿Cuanto regresa al disponible?`,
      input: "number",
      inputValue: f.pendiente,
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });
    if (!r.value) return;
    const cantidad = parseFloat(r.value);
    if (!(cantidad > 0) || cantidad > f.pendiente) {
      return Swal.fire({ icon: "error", title: `Pon una cantidad entre 0 y ${f.pendiente}` });
    }
    try {
      await axios.patch(`${URL_APARTADOS_MATERIALES}/${f._id}/liberar`, { cantidad }, cab());
      Swal.fire({ icon: "success", title: "Liberado", timer: 1500, showConfirmButton: false });
      setRecarga(recarga + 1);
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se pudo liberar", text: error.response?.data?.message || error.message });
    }
  }

  const COLUMNAS = ["Folio", "Fecha", "Pedido", "Cliente", "Tela", "Color", "Almacen", "Apartado", "Mandado", "Liberado", "Pendiente"];
  const datos = () => visibles.map((f) => [f.folio, f.fecha, f.pedido, f.cliente, f.tela, f.color, f.almacen, n(f.cantidad), n(f.consumido), n(f.liberado), n(f.pendiente)]);

  function PDF() {
    const doc = new jsPDF("landscape");
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img, "png", 230, 8, 45, 22);
    doc.text("Tela apartada para pedidos", 20, 20);
    doc.autoTable({ head: [COLUMNAS], body: datos(), startY: 34, styles: { fontSize: 8 } });
    doc.save("Apartados.pdf");
  }

  function excel() {
    new ExportJsonExcel({
      fileName: "Apartados",
      datas: [{
        sheetData: datos().map((r) => Object.fromEntries(COLUMNAS.map((c, i) => [c, r[i]]))),
        sheetName: "Apartados", sheetFilter: COLUMNAS, sheetHeader: COLUMNAS,
      }],
    }).saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_produccion ? (
        <div className="card container col-12">
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <i className="fas fa-box-open" style={{ marginRight: 8, color: "#8b5cf6" }} />
                Apartados de Tela
              </h3>
              <p className="page-subtitle">Metros apartados por pedido; se consumen al mandarle tela al maquilero del pedido</p>
            </div>
            <div>
              <Button color="success" size="sm" href="/ApartadosCreate" style={{ marginRight: 6 }}>
                Nuevo Apartado
              </Button>
              <Button color="danger" size="sm" onClick={PDF} style={{ marginRight: 6 }}>PDF</Button>
              <Button color="success" size="sm" onClick={excel}>Excel</Button>
            </div>
          </div>
          <Row style={{ marginBottom: 12 }}>
            <Col md={5}>
              <Label>Buscar</Label>
              <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Folio, pedido, cliente, tela o color" />
            </Col>
            <Col md={4} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button size="sm" color={soloPendientes ? "primary" : "secondary"} onClick={() => setSoloPendientes(!soloPendientes)}>
                {soloPendientes ? "Viendo solo con pendiente" : "Viendo todos"}
              </Button>
            </Col>
          </Row>
          {cargando ? <Spinner /> : (
            <Table size="sm" striped borderless responsive>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Fecha</th>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Tela</th>
                  <th>Color</th>
                  <th>Almacen</th>
                  <th style={{ textAlign: "right" }}>Apartado</th>
                  <th style={{ textAlign: "right" }}>Mandado</th>
                  <th style={{ textAlign: "right" }}>Liberado</th>
                  <th style={{ textAlign: "right" }}>Pendiente</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibles.map((f) => (
                  <tr key={f._id}>
                    <td>{f.folio}</td>
                    <td>{f.fecha}</td>
                    <td>{f.pedido}</td>
                    <td>{f.cliente}</td>
                    <td>{f.tela}</td>
                    <td>{f.color}</td>
                    <td>{f.almacen}</td>
                    <td style={{ textAlign: "right" }}>{n(f.cantidad)}</td>
                    <td style={{ textAlign: "right" }}>{n(f.consumido)}</td>
                    <td style={{ textAlign: "right" }}>{n(f.liberado)}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{n(f.pendiente)}</td>
                    <td>
                      {f.pendiente > 0 ? (
                        <Button size="sm" color="warning" onClick={() => liberar(f)}>Liberar</Button>
                      ) : undefined}
                    </td>
                  </tr>
                ))}
                {!visibles.length ? (
                  <tr><td colSpan={12} className="text-muted">No hay tela apartada.</td></tr>
                ) : undefined}
              </tbody>
            </Table>
          )}
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoApartados;
